// client/src/components/docs/ApiSetupPage.js
import React from 'react';
import {
  Container, Box, Typography, Paper, Chip, Divider,
  List, ListItem, ListItemText, Alert,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyIcon from '@mui/icons-material/Key';

function ExternalLink({ href, children }) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}
    >
      {children}
      <OpenInNewIcon sx={{ fontSize: '0.85em' }} />
    </Box>
  );
}

function ServiceCard({ title, badge, badgeColor = 'default', enables, steps, notes }) {
  return (
    <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>{title}</Typography>
        <Chip label={badge} color={badgeColor} size="small" />
      </Box>
      {enables && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          <strong>Enables:</strong> {enables}
        </Typography>
      )}
      <List dense disablePadding>
        {steps.map((step, i) => (
          <ListItem key={i} disablePadding sx={{ alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="body2" sx={{ mr: 1, minWidth: 20, color: 'text.secondary' }}>
              {i + 1}.
            </Typography>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary={step}
            />
          </ListItem>
        ))}
      </List>
      {notes && (
        <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
          <Typography variant="body2">{notes}</Typography>
        </Alert>
      )}
    </Paper>
  );
}

export default function ApiSetupPage() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <KeyIcon color="primary" sx={{ fontSize: 36 }} />
        <Typography variant="h4" fontWeight={700}>
          API Keys &amp; Service Setup
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        InterCollect integrates with several external services for search, pricing, and barcode
        lookup. Most are free. None are required to run the app — you only need keys for the
        collection types you want to use. Enter all keys in your instance at{' '}
        <strong>Admin → API Keys</strong>.
      </Typography>

      <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 4 }}>
        <strong>Works with no keys at all:</strong> Music (MusicBrainz), Books (Open Library),
        Board Games (BGG), Manga (MangaDex), Yu-Gi-Oh cards (YGOPRODeck), and Magic: The Gathering
        cards (Scryfall) all use free APIs with no registration required.
      </Alert>

      {/* === GAMES === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Games &amp; Consoles
      </Typography>

      <ServiceCard
        title="RAWG"
        badge="Free tier available"
        badgeColor="success"
        enables="Video game search and metadata"
        steps={[
          <span>Go to <ExternalLink href="https://rawg.io/apidocs">rawg.io/apidocs</ExternalLink> and click <strong>Get API Key</strong>.</span>,
          'Create a free account and register your application.',
          'Copy the API key shown on your developer dashboard.',
          <span>In your InterCollect instance: <strong>Admin → API Keys → RAWG → API Key</strong>.</span>,
        ]}
        notes="The free tier allows 20,000 requests/month, which is more than enough for typical personal use."
      />

      <ServiceCard
        title="IGDB (via Twitch)"
        badge="Free"
        badgeColor="success"
        enables="Game search (alternative to RAWG) and console search"
        steps={[
          <span>Go to <ExternalLink href="https://dev.twitch.tv/console">dev.twitch.tv/console</ExternalLink> and log in (or create a free Twitch account).</span>,
          <span>Click <strong>Register Your Application</strong>. Set the OAuth Redirect URL to <code>http://localhost</code> and Category to <em>Application Integration</em>.</span>,
          'After registering, click Manage on the app entry.',
          <span>Copy the <strong>Client ID</strong> shown, then click <strong>New Secret</strong> to generate and copy the <strong>Client Secret</strong>.</span>,
          <span>In your InterCollect instance: <strong>Admin → API Keys → IGDB → Client ID</strong> and <strong>Client Secret</strong>.</span>,
        ]}
        notes="IGDB is free and owned by Twitch. The Client ID and Secret are your Twitch Developer app credentials — IGDB uses Twitch authentication."
      />

      <Divider sx={{ my: 4 }} />

      {/* === MOVIES === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Movies
      </Typography>

      <ServiceCard
        title="OMDB"
        badge="Free tier available"
        badgeColor="success"
        enables="Movie and TV show search and metadata"
        steps={[
          <span>Go to <ExternalLink href="https://www.omdbapi.com/apikey.aspx">omdbapi.com/apikey.aspx</ExternalLink>.</span>,
          'Choose the Free tier (1,000 requests/day) and enter your email address.',
          'Check your email and click the activation link.',
          <span>In your InterCollect instance: <strong>Admin → API Keys → OMDB → API Key</strong>.</span>,
        ]}
        notes="1,000 requests/day is sufficient for personal use. The Patron tier ($1/month) raises this to 100,000/day if needed."
      />

      <Divider sx={{ my: 4 }} />

      {/* === LEGO === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        LEGO
      </Typography>

      <ServiceCard
        title="Rebrickable"
        badge="Free"
        badgeColor="success"
        enables="LEGO set search and metadata"
        steps={[
          <span>Go to <ExternalLink href="https://rebrickable.com/users/_/settings/#api">rebrickable.com</ExternalLink> — you need a free account.</span>,
          <span>Once logged in, navigate to <strong>Settings → API</strong> (or visit the link above).</span>,
          'Click Generate New Key.',
          <span>In your InterCollect instance: <strong>Admin → API Keys → Rebrickable → API Key</strong>.</span>,
        ]}
      />

      <ServiceCard
        title="Brickset"
        badge="Free"
        badgeColor="success"
        enables="LEGO barcode (EAN) scanning — links physical LEGO box barcodes to set data"
        steps={[
          <span>Go to <ExternalLink href="https://brickset.com/api">brickset.com/api</ExternalLink> and click <strong>Request API key</strong>.</span>,
          'Fill in the short form. Keys are usually granted the same day.',
          <span>In your InterCollect instance: <strong>Admin → API Keys → Brickset → API Key</strong>.</span>,
        ]}
        notes="Only needed if you want barcode scanning for LEGO sets. The app works without it — you'll just search by name instead of barcode."
      />

      <Divider sx={{ my: 4 }} />

      {/* === MUSIC === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Music
      </Typography>

      <ServiceCard
        title="Discogs"
        badge="Free"
        badgeColor="success"
        enables="Vinyl record search with pressing, RPM, and condition details"
        steps={[
          <span>Log in to <ExternalLink href="https://www.discogs.com">discogs.com</ExternalLink> (or create a free account).</span>,
          <span>Go to <ExternalLink href="https://www.discogs.com/settings/developers">discogs.com/settings/developers</ExternalLink>.</span>,
          <span>Scroll to <strong>Personal Access Token</strong> and click <strong>Generate new token</strong>.</span>,
          <span>In your InterCollect instance: <strong>Admin → API Keys → Discogs → Personal Access Token</strong>.</span>,
        ]}
        notes="MusicBrainz (the default music search) requires no key. Discogs is only needed if you want vinyl-specific search with pressing details."
      />

      <Divider sx={{ my: 4 }} />

      {/* === TRADING CARDS === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Trading Cards
      </Typography>

      <ServiceCard
        title="Pokémon TCG API"
        badge="Optional"
        badgeColor="default"
        enables="Pokémon card search — works without a key but at lower rate limits"
        steps={[
          <span>Go to <ExternalLink href="https://pokemontcg.io">pokemontcg.io</ExternalLink> and click <strong>Get API Key</strong>.</span>,
          'Sign up with an email address.',
          <span>Your key will be shown in your dashboard. Copy it.</span>,
          <span>In your InterCollect instance: <strong>Admin → API Keys → Pokémon TCG → API Key</strong>.</span>,
        ]}
        notes="Without a key: 1,000 requests/day. With a free key: 20,000 requests/day. Yu-Gi-Oh (YGOPRODeck) and MTG (Scryfall) require no key at all."
      />

      <ServiceCard
        title="ComicVine"
        badge="Required for comics"
        badgeColor="warning"
        enables="Comic issue search — comic search is disabled without this key"
        steps={[
          <span>Go to <ExternalLink href="https://comicvine.gamespot.com/api">comicvine.gamespot.com/api</ExternalLink>.</span>,
          'Log in or create a free account.',
          'Your API key is shown directly on that page once logged in.',
          <span>In your InterCollect instance: <strong>Admin → API Keys → ComicVine → API Key</strong>.</span>,
        ]}
      />

      <Divider sx={{ my: 4 }} />

      {/* === PRICING === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Pricing &amp; Valuations
      </Typography>

      <ServiceCard
        title="eBay Developer"
        badge="Free tier available"
        badgeColor="success"
        enables="Live eBay sold price lookups for games, consoles, movies, and more"
        steps={[
          <span>Go to <ExternalLink href="https://developer.ebay.com">developer.ebay.com</ExternalLink> and create a developer account.</span>,
          <span>Under <strong>Application Keys</strong>, create a new application. Use the <strong>Production</strong> keys (not Sandbox).</span>,
          <span>Copy the <strong>App ID (Client ID)</strong> and <strong>Cert ID (Client Secret)</strong>.</span>,
          <span>Set the <strong>Marketplace ID</strong> to your country code, e.g. <code>EBAY_US</code>, <code>EBAY_GB</code>, <code>EBAY_DE</code>, <code>EBAY_AU</code>.</span>,
          <span>In your InterCollect instance: <strong>Admin → API Keys → eBay</strong> — enter App ID, Cert ID, and Marketplace ID.</span>,
        ]}
        notes="The Verification Token and Deletion Endpoint URL fields are only required if eBay asks you to register a compliance endpoint (part of their marketplace account deletion policy). Most self-hosted instances can leave these blank."
      />

      <ServiceCard
        title="Exchange Rate API"
        badge="Free tier available"
        badgeColor="success"
        enables="Currency conversion for pricing (e.g. converting USD prices to your local currency)"
        steps={[
          <span>Go to <ExternalLink href="https://exchangeratesapi.io">exchangeratesapi.io</ExternalLink> and click <strong>Get Free API Key</strong>.</span>,
          'Register for the free plan.',
          'Copy the API key from your dashboard.',
          <span>In your InterCollect instance: <strong>Admin → API Keys → Exchange Rate API → API Key</strong>.</span>,
        ]}
        notes="Only needed if you want to display prices in a currency other than USD. The free tier allows 100 requests/month."
      />

      <Divider sx={{ my: 4 }} />

      {/* === BARCODE === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Barcode Scanning
      </Typography>

      <ServiceCard
        title="ScanDex"
        badge="Subscription required"
        badgeColor="warning"
        enables="Barcode scanning for video games and consoles — identifies a game from its physical barcode"
        steps={[
          'ScanDex is a third-party barcode-to-game lookup service. Contact the ScanDex operator to obtain credentials.',
          <span>You will need a <strong>Base URL</strong> (the service endpoint) and an <strong>API Token</strong>.</span>,
          <span>In your InterCollect instance: <strong>Admin → API Keys → ScanDex → Base URL</strong> and <strong>API Token</strong>.</span>,
        ]}
        notes="If you don't have ScanDex credentials, barcode scanning for games and consoles simply won't work — all other features continue normally."
      />

      <Divider sx={{ my: 4 }} />

      {/* === EMAIL === */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Email (SMTP)
      </Typography>

      <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Email settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Enables:</strong> Email verification on registration, password reset emails, and other notifications.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          InterCollect ships with <strong>Maddy</strong>, a self-contained SMTP server that runs
          as part of the Docker stack. Maddy sends email directly without a third-party relay,
          which is simplest to set up but may land in spam folders without proper DNS records.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          To configure email, go to <strong>Admin → Email Settings</strong> and enter your SMTP details:
        </Typography>
        <List dense disablePadding sx={{ mb: 2 }}>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary={<span><strong>Host:</strong> <code>intercollect-maddy</code> (to use the built-in Maddy server)</span>}
            />
          </ListItem>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary={<span><strong>Port:</strong> <code>587</code></span>}
            />
          </ListItem>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary={<span><strong>Username / Password:</strong> leave blank (Maddy on the internal network does not require auth)</span>}
            />
          </ListItem>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{ variant: 'body2' }}
              primary={<span><strong>From address:</strong> <code>noreply@yourdomain.com</code></span>}
            />
          </ListItem>
        </List>
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="body2">
            You can also use any external SMTP provider (Gmail, SendGrid, Postmark, etc.) by
            entering its host, port, username, and password in the same Email Settings page.
          </Typography>
        </Alert>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        All API keys are stored in your instance's database and never leave your server. You can
        update them at any time without restarting the application.
      </Typography>
    </Container>
  );
}
